// Cliente de GESTÃO da Central de Ajuda (exclusivo do ADMIN; o backend aplica requireStrictAdmin).
import { requestJson, requestMultipart } from "@/lib/api";
import type { HelpImageRef } from "@/lib/helpApi";

export type AdminHelpSummary = {
  id: string;
  slug: string;
  title: string;
  environment: string;
  category: string;
  moduleKey: string | null;
  screenKey: string | null;
  published: boolean;
  hasDraft: boolean;
  archived: boolean;
  lastReviewedAt: string | null;
  lastReviewedBy: string | null;
  updatedAt: string;
};

export type AdminHelpVersion = {
  id: string;
  versionNumber: number;
  title: string;
  state: string;
  authorEmail: string;
  authorRole: string;
  createdAt: string;
};

export type AdminHelpDetail = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  environment: string;
  category: string;
  moduleKey: string | null;
  screenKey: string | null;
  displayOrder: number;
  archived: boolean;
  requiredPermissions: string[];
  roleKeys: string[];
  published: boolean;
  publishedContentMarkdown: string | null;
  hasDraft: boolean;
  draftTitle: string | null;
  draftSummary: string | null;
  draftContentMarkdown: string | null;
  lastReviewedAt: string | null;
  lastReviewedBy: string | null;
  images: HelpImageRef[];
  versions: AdminHelpVersion[];
};

export type HelpArticlePayload = {
  slug?: string;
  environment: string;
  category: string;
  moduleKey?: string | null;
  screenKey?: string | null;
  displayOrder?: number | null;
  title: string;
  summary?: string | null;
  contentMarkdown: string;
  requiredPermissions: string[];
  roleKeys: string[];
};

export function listAdminHelp(): Promise<AdminHelpSummary[]> {
  return requestJson<AdminHelpSummary[]>("/api/v1/admin/help/articles");
}

export function getAdminHelp(id: string): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(`/api/v1/admin/help/articles/${id}`);
}

export function createAdminHelp(payload: HelpArticlePayload): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>("/api/v1/admin/help/articles", { method: "POST", body: payload });
}

export function updateAdminHelp(id: string, payload: HelpArticlePayload): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(`/api/v1/admin/help/articles/${id}`, { method: "PUT", body: payload });
}

export function publishAdminHelp(id: string): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(`/api/v1/admin/help/articles/${id}/publish`, { method: "POST" });
}

export function archiveAdminHelp(id: string, archived: boolean): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(
    `/api/v1/admin/help/articles/${id}/${archived ? "archive" : "unarchive"}`,
    { method: "POST" },
  );
}

export function restoreAdminHelp(id: string, versionId: string): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(`/api/v1/admin/help/articles/${id}/restore/${versionId}`, {
    method: "POST",
  });
}

export function reviewAdminHelp(id: string): Promise<AdminHelpDetail> {
  return requestJson<AdminHelpDetail>(`/api/v1/admin/help/articles/${id}/review`, { method: "POST" });
}

export function uploadAdminHelpImage(id: string, file: File): Promise<HelpImageRef> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<HelpImageRef>(`/api/v1/admin/help/articles/${id}/images`, form);
}
